import React, { useState } from "react";
import { t, Locale } from "../../i18n";

interface Session {
  id: string;
  device: string;
  browser: string;
  location: string;
  lastActive: string;
  isCurrent?: boolean;
}

const mockSessions: Session[] = [
  {
    id: "current",
    device: "MacBook Pro 14\"",
    browser: "Chrome 124",
    location: "Stockholm, Sweden",
    lastActive: "Now",
    isCurrent: true
  },
  {
    id: "session-2",
    device: "iPhone 15",
    browser: "Safari",
    location: "Stockholm, Sweden",
    lastActive: "2 hours ago"
  },
  {
    id: "session-3",
    device: "Windows 11",
    browser: "Firefox 124",
    location: "New York, USA",
    lastActive: "3 days ago"
  }
];

export function ActiveSessions(props: { locale: Locale }) {
  const locale = props.locale;
  const [sessions, setSessions] = useState<Session[]>(mockSessions);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleRevokeSession = (id: string) => {
    setSessions(sessions.filter(s => s.id !== id));
  };

  const handleRevokeAllOthers = () => {
    setSessions(sessions.filter(s => s.isCurrent));
    setShowConfirmation(false);
  };

  return (
    <div>
      <h3 style={{ marginTop: 0, fontSize: "15px", fontWeight: 600, marginBottom: "16px" }}>{t(locale, "sessions.title")}</h3>

      {/* Sessions Table */}
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "13px"
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--line)" }}>
              <th style={{ textAlign: "left", padding: "10px 0", fontWeight: 600, color: "var(--muted)", fontSize: "12px" }}>{t(locale, "sessions.device")}</th>
              <th style={{ textAlign: "left", padding: "10px 0", fontWeight: 600, color: "var(--muted)", fontSize: "12px" }}>{t(locale, "sessions.browser")}</th>
              <th style={{ textAlign: "left", padding: "10px 0", fontWeight: 600, color: "var(--muted)", fontSize: "12px" }}>{t(locale, "sessions.location")}</th>
              <th style={{ textAlign: "left", padding: "10px 0", fontWeight: 600, color: "var(--muted)", fontSize: "12px" }}>{t(locale, "sessions.lastActive")}</th>
              <th style={{ textAlign: "right", padding: "10px 0", fontWeight: 600, color: "var(--muted)", fontSize: "12px" }}>{t(locale, "sessions.action")}</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr key={session.id} style={{ borderBottom: "1px solid var(--line)", opacity: 0.8 }}>
                <td style={{ padding: "12px 0", color: "var(--text)" }}>
                  <div>{session.device}</div>
                  {session.isCurrent && (
                    <div style={{ fontSize: "11px", color: "var(--accent)", marginTop: "2px" }}>{t(locale, "sessions.thisDevice")}</div>
                  )}
                </td>
                <td style={{ padding: "12px 0", color: "var(--text)" }}>{session.browser}</td>
                <td style={{ padding: "12px 0", color: "var(--text)" }}>{session.location}</td>
                <td style={{ padding: "12px 0", color: "var(--text)" }}>{session.lastActive}</td>
                <td style={{ padding: "12px 0", textAlign: "right" }}>
                  {!session.isCurrent && (
                    <button
                      onClick={() => handleRevokeSession(session.id)}
                      style={{
                        background: "transparent",
                        color: "#ef4444",
                        border: "1px solid #ef4444",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {t(locale, "sessions.revoke")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Location footnote */}
      <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "8px", fontStyle: "italic" }}>
        {t(locale, "sessions.locationNote")}
      </div>

      {/* Revoke All Other Sessions Button */}
      {sessions.length > 1 && (
        <div style={{ marginTop: "16px" }}>
          <button
            onClick={() => setShowConfirmation(true)}
            style={{
              background: "transparent",
              color: "#ef4444",
              border: "1px solid #ef4444",
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 500
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {t(locale, "sessions.revokeAll")}
          </button>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100
          }}
          onClick={() => setShowConfirmation(false)}
        >
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: "10px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "var(--shadow)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: "8px", fontSize: "16px" }}>{t(locale, "sessions.revokeAll.confirm.title")}</h3>
            <p style={{ color: "var(--muted)", fontSize: "13px", marginBottom: "20px" }}>
              {t(locale, "sessions.revokeAll.confirm.description")}
            </p>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  background: "transparent",
                  color: "var(--text)",
                  border: "1px solid var(--control-border)",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 500
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--hover-bg)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {t(locale, "common.cancel")}
              </button>
              <button
                onClick={handleRevokeAllOthers}
                style={{
                  background: "#ef4444",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 500
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                {t(locale, "sessions.revoke")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
